import { StartTimeRange } from "@/shared/model/enums/start-time-range";
import { sortBy as _sortBy } from "lodash-es";
import { OutcomeMarket } from "@/shared/model/outcome";
import { EmptyMarket } from "@/shared/model/market";
import useSazkaSnackbar from "@/shared/composables/use-sazka-snackbar";
import { Snackbar } from "../model/snackbar";
import { VNode, VNodeChild } from "vue";
import { Team } from "../model/team";

interface TicketDate {
    dayDate: string;
    time: string;
}

export default function useUtils() {
    const formatPrice = (price: number, forceTwoDigits?: boolean): string => {
        const fractionDigits = forceTwoDigits ? 2 : price % 1 === 0 ? 0 : 2;

        const format = new Intl.NumberFormat("cs-CZ", {
            style: "currency",
            currency: "CZK",
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
        });

        return format.format(price);
    };

    const formatPriceLite = (price: string | number, decimals: number = 0, useDot: boolean = false) => {
        const formattedPrice = Number(price).toLocaleString("cs-CZ", {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });

        const priceWithCorrectSeparator = useDot ? formattedPrice.replace(",", ".") : formattedPrice;

        return priceWithCorrectSeparator + " Kč";
    };

    const formatTime = (timeInSeconds: number): string => {
        const hours = Math.floor(timeInSeconds / 3600);
        const minutes = Math.floor((timeInSeconds - hours * 3600) / 60);
        const seconds = timeInSeconds - hours * 3600 - minutes * 60;

        let timeString = "";
        if (hours > 0) {
            timeString += hours.toString().padStart(2, "0") + ":";
        }
        timeString += minutes.toString().padStart(2, "0") + ":";
        timeString += seconds.toString().padStart(2, "0");

        return timeString;
    };

    const formatDate = (dat, format = "default"): TicketDate => {
        if (!dat) return null;
        const date = new Date(dat);
        const today = new Date();

        const daysCzech = ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"];
        const dayOfWeek = daysCzech[date.getDay()];

        const hours = date.getHours().toString().padStart(2, "0");
        const minutes = date.getMinutes().toString().padStart(2, "0");
        const time = `${hours}:${minutes}`;

        let dayDate;
        if (format == "default") {
            dayDate =
                date.toDateString() === today.toDateString()
                    ? "Dnes"
                    : `${dayOfWeek} ${date.getDate()}. ${date.getMonth() + 1}. ${date.getFullYear().toString().slice(-2)}`;
        } else if (format == "simple") {
            dayDate = `${date.getDate()}. ${date.getMonth() + 1}.`;
        }

        return {
            dayDate: dayDate,
            time: time,
        };
    };

    const elapsedTime = (startTime: number): number => {
        return Math.floor((new Date().getTime() - startTime) / 1000);
    };

    const loggedInDuration = (logonTime: number) => {
        if (!logonTime) return null;

        const diff = new Date().getTime() - logonTime;

        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000)
            .toString()
            .padStart(2, "0");

        return `${minutes}:${seconds}`;
    };

    const formatRate = (rate: number): string => {
        if (rate < 100) {
            return rate.toFixed(2).toString();
        } else {
            return Math.floor(rate).toString();
        }
    };

    const toTitleCase = (str: string, firstLetterOnly: boolean = true): string => {
        if (!str) return "";
        return firstLetterOnly
            ? // capitalizing the first letter only while keeping the rest of the words/letters lowercase
              str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
            : // capitalizing the first letter of each word while keeping the rest of the letters lowercase
              str
                  .toLowerCase()
                  .split(" ")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ");
    };

    // Use to 'normalize' inconsistencies in market naming coming from backend
    const normalizeMarketName = (str: string, teams?: Team[]) => {
        if (!str) return "";

        // Regex to match "Tým" followed by a number
        const teamRegex = /Tým (\d+)/g;

        // Replace "Tým 1" with the home team name and "Tým 2" with the away team name
        str = str.replace(teamRegex, (match, p1) => {
            if (p1 === "1") {
                return teams?.find((team) => team.side === "HOME")?.name || match;
            } else if (p1 === "2") {
                return teams?.find((team) => team.side === "AWAY")?.name || match;
            }
            return match; // If the number is not 1 or 2, return the original match
        });

        // capitalize the first letter only and the rest of the words keep lowercase (all letters)
        if (str === str.toUpperCase()) {
            return toTitleCase(str);
        } else {
            // Market name can contain name of the team (need to keep it as is), some words starts with capitals in the middle of the market name, and we need to replace them
            const replaceRegExp = [/^Bod.?:?$/g, /^Gól.?:?$/g, /^Hr.:?$/g, /^Přesný$/g, /^Set.?:?$/g, /^Vítěz$/g];
            const strSplit = str.split(" ");
            return strSplit.length > 1
                ? strSplit
                      .map((word, idx) =>
                          idx > 0 && replaceRegExp.some((regExp) => word.match(regExp)) ? word.toLowerCase() : word,
                      )
                      .join(" ")
                : toTitleCase(str);
        }
    };

    const normalizeText = (str: string): string => {
        if (!str) return "";
        // Normalize the text by removing diacritics and converting to lowercase
        return str
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase();
    };

    //display order is used only for sorting items with negative one - applied on sports and leagues
    const sortByDisplayOrder = <T extends { displayOrder: number; name?: string }>(items: T[]): T[] => {
        // Sorts by 'displayOrder' and if displayOrder is equal then by 'startTime' and if startTime is equal then by 'name'
        return _sortBy(items, ["displayOrder", "startTime", "name"]);
    };

    const sortByNegativeDisplayOrder = <T extends { displayOrder: number; name?: string }>(items: T[]): T[] => {
        return items.toSorted((a, b) => {
            if (a.displayOrder < 0 && b.displayOrder < 0) {
                return a.displayOrder - b.displayOrder;
            } else if (a.displayOrder >= 0 && b.displayOrder >= 0) {
                if (a.name && b.name) {
                    return a.name.localeCompare(b.name);
                }
                return 0;
            } else if (a.displayOrder < 0) {
                return -1;
            } else {
                return 1;
            }
        });
    };

    function isEventInTimeRange(filter, eventStartTime) {
        const eventDate = new Date(eventStartTime);
        const now = new Date();
        let start, end;

        if (!filter) {
            // return eventDate >= new Date(now.setHours(0, 0, 0, 0)); //show only future events
            return true;
        }

        switch (filter) {
            case StartTimeRange.Today:
                start = new Date(now.setHours(0, 0, 0, 0));
                end = new Date(now.setHours(23, 59, 59, 999));
                break;
            case StartTimeRange.OneDay:
                start = new Date(now);
                end = new Date(now.setHours(now.getHours() + 24));
                break;
            case StartTimeRange.OneHour:
                start = new Date(now);
                end = new Date(now.setHours(now.getHours() + 1));
                break;
            case StartTimeRange.ThreeHours:
                start = new Date(now);
                end = new Date(now.setHours(now.getHours() + 3));
                break;
            case StartTimeRange.SixHours:
                start = new Date(now);
                end = new Date(now.setHours(now.getHours() + 6));
                break;
            case StartTimeRange.TwelveHours:
                start = new Date(now);
                end = new Date(now.setHours(now.getHours() + 12));
                break;
            case StartTimeRange.Tomorrow:
                start = new Date(now);
                start.setDate(start.getDate() + 1);
                start.setHours(0, 0, 0, 0);
                end = new Date(start);
                end.setHours(23, 59, 59, 999);
                break;
            case StartTimeRange.TwoDays:
                start = new Date(now);
                end = new Date(now.setHours(now.getHours() + 48));
                break;
            default:
                start = new Date();
                end = new Date();
        }
        return eventDate >= start && eventDate <= end;
    }

    // Simple type guard based on presumption that only EmptyMarket has 'empty' and is set to true
    const isEmptyOutcome = (outcome: OutcomeMarket | EmptyMarket): outcome is EmptyMarket => {
        return ![undefined, false].includes((outcome as EmptyMarket).empty);
    };

    const defaultCopySnackbar: Snackbar = {
        message: "Text byl úspěšně zkopírován",
        type: "success",
        duration: 4000,
        icon: "copy",
    };

    const defaultCopyErrorSnackbar: Snackbar = {
        message: "Nepovedlo se zkopírovat text",
        type: "error",
        duration: 4000,
        icon: "close",
    };

    function copyTextToClipboard(
        textToCopy: string,
        successSnackbarOptions: Partial<Snackbar> = {},
        errorSnackbarOptions: Partial<Snackbar> = {},
    ): void {
        const successOptions = { ...defaultCopySnackbar, ...successSnackbarOptions };
        const errorOptions = { ...defaultCopyErrorSnackbar, ...errorSnackbarOptions };

        navigator.clipboard
            .writeText(textToCopy)
            .then(() => {
                useSazkaSnackbar().createSnackbar(successOptions);
            })
            .catch(() => {
                useSazkaSnackbar().createSnackbar(errorOptions);
            });
    }

    function extractTextFromVNode(vnode: VNodeChild): string {
        if (typeof vnode === "string") {
            return vnode;
        } else if (Array.isArray(vnode)) {
            return vnode.map((child) => extractTextFromVNode(child as VNodeChild)).join("");
        } else if (typeof vnode === "object" && vnode !== null) {
            const vnodeObj = vnode as VNode;
            if (vnodeObj.children) {
                return extractTextFromVNode(vnodeObj.children as unknown as VNodeChild);
            }
        }

        return "";
    }

    function adjustCzechWordingByCount(key: string, num: number) {
        switch (key) {
            case "moznost":
                return num === 1
                    ? `${num} další možnost`
                    : num < 5
                      ? `${num} další možnosti`
                      : `${num} dalších možností`;
            case "prilezitost":
                return num === 1
                    ? `${num} další příležitost`
                    : num < 5
                      ? `${num} další příležitosti`
                      : `${num} dalších příležitostí`;
            default: {
                return key;
            }
        }
    }

    return {
        formatPrice,
        formatPriceLite,
        formatTime,
        formatDate,
        elapsedTime,
        formatRate,
        toTitleCase,
        normalizeMarketName,
        isEventInTimeRange,
        sortByDisplayOrder,
        sortByNegativeDisplayOrder,
        isEmptyOutcome,
        normalizeText,
        loggedInDuration,
        copyTextToClipboard,
        extractTextFromVNode,
        adjustCzechWordingByCount,
    };
}
